import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { useTranslate } from "@tolgee/react";
import copy from "copy-to-clipboard";

import MeetingDetailsHeader from "../Meetings/MeetingDetailsHeader";
import TimePicker from "../Meetings/TimePicker";
import MeetingDateTimePicker from "../Meetings/MeetingDateTimePicker";
import ConferenceParticipants from "./ConferenceParticipants";
import { ReactComponent as RightArrow } from "../../Assets/icons/rightArrow.svg";
import QRCode from "react-qr-code";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import PrimaryButtonRemove from "../GenericComponents/PrimaryButtonRemove";
import DeleteConfirmationChild from "../GenericComponents/DeleteConfirmationChild";
import { useNotify } from "../../Helpers/Notifications";
import { CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER, CONFERENCE_ROLE_ATTENDEE, CONFERENCE_REGISTRATION_STATUS_APPROVED, CONFERENCE_REGISTRATION_STATUS_DENIED, CONFERENCE_REGISTRATION_STATUS_PENDING, WEB_BASE_URL } from "../../Constants/constantValues";
import { useAuthService } from "../../Context/AuthContext";
import { useConferencesService } from "../../Context/ConferenceContext";
import PollManagementModal from './PollManagementModal';

const ConferenceDetailsCard = ({
  conference,
  handleCloseConferenceDetails,
  handleDeleteConferenceBtn,
  handleUpdateConferenceDetails,
  handleJoinConference,
  contacts,
}) => {
  const { notifySuccess, notifyError } = useNotify();
  const { t } = useTranslate();
  const { user } = useAuthService();
  const {
    listConferenceRegistrations,
    approveConferenceRegistration,
    denyConferenceRegistration,
    getConferencePolls,
  } = useConferencesService();

  const [editParticipants, setEditParticipants] = useState(false);
  const [updateBtnDisabled, setUpdateBtnDisabled] = useState(true);

  const [title, setTitle] = useState(conference.title);
  const [description, setDescription] = useState(conference.description);
  const [termsAndConditions, setTermsAndConditions] = useState(conference.terms_and_conditions || "");
  const settings = conference.settings || {};
  const publicRegistration =
    settings && settings.public_registration !== undefined
      ? !!settings.public_registration
      : true;
  const registrationPath = publicRegistration
    ? `/public/conferences/${conference.id}/registration`
    : `/conferences/${conference.id}/registration`;
  const shareLink =
    conference.share_link || `${WEB_BASE_URL}/#${registrationPath}`;

  const safeDate = (value) => {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  const startDate = safeDate(conference.start_at);
  const endDate = safeDate(conference.end_at);

  const [conferenceStartDate, setConferenceStartDate] = useState(startDate);
  const [conferenceEndDate, setConferenceEndDate] = useState(endDate);
  const [isJoinWindowOpen, setIsJoinWindowOpen] = useState(false);

  const now = new Date();
  const startDateClone = new Date(conferenceStartDate);
  const isStartToday = startDateClone.toDateString() === now.toDateString();
  const minStartTime = isStartToday
    ? now
    : new Date(startDateClone.setHours(0, 0, 0, 0));

  const minEndDateTime =
    conferenceEndDate.toDateString() === conferenceStartDate.toDateString()
      ? new Date(Math.max(conferenceStartDate.getTime(), now.getTime()))
      : now;

  const calculateMinEndTime = () => {
    const endDateClone = new Date(conferenceEndDate);
    if (endDateClone.toDateString() === conferenceStartDate.toDateString()) {
      return minEndDateTime;
    }
    if (endDateClone.toDateString() === now.toDateString()) {
      return now;
    }
    return null;
  };

  const [organizers, setOrganizers] = useState([]);
  const [initialOrganizers, setInitialOrganizers] = useState([]);

  const [presenters, setPresenters] = useState([]);
  const [initialPresenters, setInitialPresenters] = useState([]);

  const [otherParticipants, setOtherParticipants] = useState([]);
  const [initialOtherParticipants, setInitialOtherParticipants] = useState([]);
  const [deleteConferencePopup, setDeleteConferencePopup] = useState(false);
  const [activeRole, setActiveRole] = useState(CONFERENCE_ROLE_PRESENTER);
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationActionsLoading, setRegistrationActionsLoading] = useState({});
  const headerImageInputRef = useRef(null);
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [headerImageRemoved, setHeaderImageRemoved] = useState(false);
  const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState(null);

  useEffect(() => {
    if (!headerImageFile) {
      setHeaderImagePreviewUrl(null);
      return undefined;
    }
    const previewUrl = URL.createObjectURL(headerImageFile);
    setHeaderImagePreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [headerImageFile]);

  const organizerData = conference.organizers || [];
  const participantEntries = conference.participants || [];
  const presenterUsers = participantEntries
    .filter((p) => p.role === CONFERENCE_ROLE_PRESENTER && p.user)
    .map((p) => p.user);
  const isCurrentOrganizer =
    user && organizerData.some((item) => String(item.id) === String(user.id));
  const isCurrentPresenter =
    user && presenterUsers.some((item) => String(item.id) === String(user.id));
  const canEditConference = !!(isCurrentOrganizer || isCurrentPresenter);
  const canEditTerms = !!isCurrentOrganizer;
  const canEditOrganizers = !!isCurrentOrganizer;
  const [allowMic, setAllowMic] = useState(!!settings.allow_mic);
  const [allowCamera, setAllowCamera] = useState(!!settings.allow_camera);
  const [allowChat, setAllowChat] = useState(!!settings.allow_chat);
  const [allowPolls, setAllowPolls] = useState(!!settings.allow_polls);
  const [allowInPersonJoining, setAllowInPersonJoining] = useState(!!settings.allow_in_person_joining);
  const [allowAutoApprove, setAllowAutoApprove] = useState(!!settings.auto_approve_participants);
  const [inviteAllOrgUsers, setInviteAllOrgUsers] = useState(true);
  const [managePollsOpen, setManagePollsOpen] = useState(false);
  const [pollsCount, setPollsCount] = useState(0);
  const hasHeaderImage =
    !headerImageRemoved && !!(conference?.header_image || headerImagePreviewUrl);

  const pendingRegistrationsCount = registrations.filter(
    (registration) => registration.status === CONFERENCE_REGISTRATION_STATUS_PENDING
  ).length;

  const isCurrentAttendee = useMemo(() => {
    if (!user) return false;
    return participantEntries.some(
      (participant) =>
        participant.role === CONFERENCE_ROLE_ATTENDEE &&
        participant.user &&
        String(participant.user.id) === String(user.id)
    );
  }, [participantEntries, user]);

  const showJoinButton = isCurrentOrganizer || isCurrentPresenter || isJoinWindowOpen;

  const normalizeInviteeEmail = (value) => (value ? String(value).trim().toLowerCase() : "");

  const combinedInviteeLabels = useMemo(() => {
    const registrationEmails = new Set(
      registrations.map((registration) => normalizeInviteeEmail(registration.email))
    );

    const registrationLabels = registrations
      .filter((registration) => registration.status === CONFERENCE_REGISTRATION_STATUS_APPROVED)
      .map((registration) => registration.name || registration.email || "")
      .filter(Boolean);

    const selectedLabels = otherParticipants
      .filter((participant) => {
        const identifier =
          typeof participant === "string" ? participant : participant?.email;
        return !registrationEmails.has(normalizeInviteeEmail(identifier));
      })
      .map((participant) => {
        if (typeof participant === "string") return participant.trim();
        return participant?.name || participant?.email || "";
      })
      .filter(Boolean);

    return [...registrationLabels, ...selectedLabels];
  }, [otherParticipants, registrations]);

  const invitees = combinedInviteeLabels;

  const syncAttendeesFromConference = (conf) => {
    if (!conf) return;
    const attendeeParticipants = (conf.participants || [])
      .filter((p) => p.role === CONFERENCE_ROLE_ATTENDEE && p.user)
      .map((p) => p.user?.email || p.user?.name || "")
      .filter(Boolean);
    const uniqueAttendees = Array.from(new Set(attendeeParticipants));
    setOtherParticipants(uniqueAttendees);
    setInitialOtherParticipants(uniqueAttendees);
  };

  const updateRegistrationStatus = (registrationId, status) => {
    setRegistrations((prev) =>
      prev.map((registration) =>
        registration.id === registrationId ? { ...registration, status } : registration
      )
    );
  };

  const setRegistrationActionState = (registrationId, isLoading) => {
    setRegistrationActionsLoading((prev) => {
      const next = { ...prev };
      if (isLoading) {
        next[registrationId] = true;
      } else {
        delete next[registrationId];
      }
      return next;
    });
  };

  const loadRegistrations = useCallback(() => {
    if (!conference?.id || !canEditConference) {
      setRegistrations([]);
      return;
    }

    setRegistrationsLoading(true);
    listConferenceRegistrations(
      conference.id,
      (response) => {
        setRegistrations(response?.data?.registrations || []);
        setRegistrationsLoading(false);
      },
      () => {
        setRegistrationsLoading(false);
      }
    );
  }, [conference?.id, canEditConference, listConferenceRegistrations]);

  useEffect(() => {
    const safeMoment = (value) => {
      const m = moment(value);
      return m.isValid() ? m : moment();
    };

    const updateJoinWindow = () => {
      const startAt = safeMoment(conference.start_at);
      const endAt = conference.end_at
        ? safeMoment(conference.end_at)
        : startAt.clone().add(60, "minutes");
      const joinStartAt = startAt.clone().subtract(1, "minutes");
      const now = moment();

      setIsJoinWindowOpen(
        now.isSameOrAfter(joinStartAt) && now.isBefore(endAt)
      );
    };

    updateJoinWindow();
    const interval = setInterval(updateJoinWindow, 30000);
    return () => clearInterval(interval);
  }, [conference.start_at, conference.end_at]);

  // Only re-order the list; do not inject the current user if they aren't already an organizer.
  const placeCurrentUserFirst = (list) => {
    if (!user) return list;
    const userId = String(user.id);
    const hasUser = (list || []).some((item) => String(item?.id) === userId);
    if (!hasUser) return list;
    const filtered = (list || []).filter((item) => String(item?.id) !== userId);
    return [user, ...filtered];
  };

  const initializeData = () => {
    if (!contacts || !conference) {
      return;
    }

    const orgUsers = conference.organizers || [];

    setOrganizers(placeCurrentUserFirst(orgUsers));
    setInitialOrganizers(placeCurrentUserFirst(orgUsers));

    setPresenters(presenterUsers);
    setInitialPresenters(presenterUsers);

    syncAttendeesFromConference(conference);

    setTitle(conference.title);
    setDescription(conference.description);
    setTermsAndConditions(conference.terms_and_conditions || "");
    setConferenceStartDate(safeDate(conference.start_at));
    setConferenceEndDate(safeDate(conference.end_at));
    const nextSettings = conference.settings || {};
    setAllowMic(!!nextSettings.allow_mic);
    setAllowCamera(!!nextSettings.allow_camera);
    setAllowChat(!!nextSettings.allow_chat);
    setAllowPolls(!!nextSettings.allow_polls);
    setAllowInPersonJoining(!!nextSettings.allow_in_person_joining);
    setAllowAutoApprove(!!nextSettings.auto_approve_participants);
    setInviteAllOrgUsers(true);
    setHeaderImageFile(null);
    setHeaderImageRemoved(false);
    if (headerImageInputRef.current) headerImageInputRef.current.value = "";
  };

  const ensureCurrentUserOrganizer = () => {
    if (!user || !isCurrentOrganizer) return;
    setOrganizers((prev) => {
      if (prev.find((item) => item.id === user.id)) return placeCurrentUserFirst(prev);
      return [user, ...prev];
    });
    setInitialOrganizers((prev) => {
      if (prev.find((item) => item.id === user.id)) return placeCurrentUserFirst(prev);
      return [user, ...prev];
    });
  };

  const handleBackBtn = () => {
    setOrganizers(initialOrganizers);
    setPresenters(initialPresenters);
    setOtherParticipants(initialOtherParticipants);
    setEditParticipants(false);
  };

  const handleApproveRegistrationAction = (registration) => {
    if (!registration?.id || !conference?.id) return;
    setRegistrationActionState(registration.id, true);
    approveConferenceRegistration(
      conference.id,
      registration.id,
      (response) => {
        const updatedConference = response?.data?.conference;
        if (updatedConference) {
          syncAttendeesFromConference(updatedConference);
        }
        updateRegistrationStatus(registration.id, CONFERENCE_REGISTRATION_STATUS_APPROVED);
        notifySuccess(t('conferences.registration_approved'));
        setRegistrationActionState(registration.id, false);
      },
      () => {
        notifyError(t("general.something_wrong"));
        setRegistrationActionState(registration.id, false);
      }
    );
  };

  const handleDenyRegistrationAction = (registration) => {
    if (!registration?.id || !conference?.id) return;
    setRegistrationActionState(registration.id, true);
    denyConferenceRegistration(
      conference.id,
      registration.id,
      (response) => {
        const updatedConference = response?.data?.conference;
        if (updatedConference) {
          syncAttendeesFromConference(updatedConference);
        }
        updateRegistrationStatus(registration.id, CONFERENCE_REGISTRATION_STATUS_DENIED);
        notifySuccess(t('conferences.registration_denied'));
        setRegistrationActionState(registration.id, false);
      },
      () => {
        notifyError(t("general.something_wrong"));
        setRegistrationActionState(registration.id, false);
      }
    );
  };

  const handleUninviteRegistrationAction = (registration) => {
    if (!registration?.id || !conference?.id) return;
    setRegistrationActionState(registration.id, true);
    denyConferenceRegistration(
      conference.id,
      registration.id,
      (response) => {
        const updatedConference = response?.data?.conference;
        if (updatedConference) {
          syncAttendeesFromConference(updatedConference);
        }
        updateRegistrationStatus(registration.id, CONFERENCE_REGISTRATION_STATUS_DENIED);
        notifySuccess(t('conferences.invite_removed'));
        setRegistrationActionState(registration.id, false);
      },
      () => {
        notifyError(t("general.something_wrong"));
        setRegistrationActionState(registration.id, false);
      }
    );
  };

  const checkUpdateBtnDisabled = () => {
    const titleChanged = title !== conference.title;
    const descriptionChanged = description !== conference.description;
    const termsChanged = termsAndConditions !== (conference.terms_and_conditions || "");
    const headerImageChanged = !!headerImageFile || headerImageRemoved;

    const organizerIds = new Set(organizers.map((p) => String(p.id)));
    const initialOrganizerIds = new Set(initialOrganizers.map((p) => String(p.id)));

    const presenterIds = new Set(presenters.map((p) => String(p.id)));
    const initialPresenterIds = new Set(initialPresenters.map((p) => String(p.id)));

    const contactChanged =
      !areSetsEqual(organizerIds, initialOrganizerIds) ||
      !areSetsEqual(presenterIds, initialPresenterIds);

    const startDateChanged = !moment(conferenceStartDate).isSame(startDate);
    const endDateChanged = !moment(conferenceEndDate).isSame(endDate);

    const otherParticipantChanged = !arraysEqual(
      otherParticipants,
      initialOtherParticipants
    );

    const settingsChanged =
      allowMic !== settings.allow_mic ||
      allowCamera !== settings.allow_camera ||
      allowChat !== settings.allow_chat ||
      allowPolls !== settings.allow_polls ||
      allowInPersonJoining !== settings.allow_in_person_joining ||
      allowAutoApprove !== settings.auto_approve_participants ||
      inviteAllOrgUsers !== settings.invite_all_org_users;

    if (
      titleChanged ||
      descriptionChanged ||
      termsChanged ||
      headerImageChanged ||
      contactChanged ||
      otherParticipantChanged ||
      endDateChanged ||
      startDateChanged ||
      settingsChanged
    ) {
      setUpdateBtnDisabled(false);
    } else {
      setUpdateBtnDisabled(true);
    }
  };

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const aSorted = [...a].sort();
    const bSorted = [...b].sort();
    return aSorted.every((val, index) => val === bSorted[index]);
  };

  const areSetsEqual = (setA, setB) => {
    if (setA.size !== setB.size) return false;
    for (const val of setA) {
      if (!setB.has(val)) return false;
    }
    return true;
  };

  const handleShareBtn = () => {
    if (!shareLink) return;
    copy(shareLink);
    notifySuccess(t('misc.link_copied'));
  };

  const formatOrganizer = (organizer) => {
    if (!organizer) {
      return "";
    }
    if (organizer.name && organizer.email) {
      return `${organizer.name} (${organizer.email})`;
    }
    return organizer.name || organizer.email || "";
  };

  function auto_grow(element) {
    element.style.height = "5px";
    element.style.height = element.scrollHeight + "px";
  }

  useEffect(() => {
    let element = document.getElementById("resizeTa-4");
    if (element) {
      auto_grow(element);
    }
    const termsElement = document.getElementById("resizeTa-terms-4");
    if (termsElement) {
      auto_grow(termsElement);
    }
  }, [description, termsAndConditions]);

  useEffect(() => {
    initializeData();
  }, [conference, contacts]);

  const handleHeaderImageChange = (e) => {
    const file = e.currentTarget.files?.[0] || null;
    setHeaderImageFile(file);
    setHeaderImageRemoved(false);
  };

  const clearHeaderImage = () => {
    setHeaderImageFile(null);
    setHeaderImageRemoved(true);
    if (headerImageInputRef.current) headerImageInputRef.current.value = "";
  };

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations, conference]);

  useEffect(() => {
    if (!conference?.id || !canEditConference) return undefined;
    const intervalId = setInterval(loadRegistrations, 15000);
    return () => clearInterval(intervalId);
  }, [conference?.id, canEditConference, loadRegistrations]);

  useEffect(() => {
    ensureCurrentUserOrganizer();
  }, [user]);

  useEffect(() => {
    checkUpdateBtnDisabled();
  }, [
    organizers,
    presenters,
    initialOtherParticipants,
    initialOrganizers,
    initialPresenters,
    otherParticipants,
    title,
    description,
    termsAndConditions,
    headerImageFile,
    headerImageRemoved,
    conferenceStartDate,
    conferenceEndDate,
    allowPolls,
    allowInPersonJoining,
    allowChat,
    allowAutoApprove
  ]);

  useEffect(() => {
    const startDate = new Date(conferenceStartDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(conferenceEndDate);
    endDate.setHours(0, 0, 0, 0);

    const startTime = conferenceStartDate.getTime();
    const endTime = conferenceEndDate.getTime();

    if (endDate < startDate) {
      setConferenceEndDate(new Date(conferenceStartDate));
    }

    if (endDate.getTime() === startDate.getTime() && endTime < startTime) {
      const updatedEndDate = new Date(conferenceStartDate);
      updatedEndDate.setMinutes(conferenceStartDate.getMinutes() + 15);
      setConferenceEndDate(updatedEndDate);
    }
  }, [conferenceStartDate, conferenceEndDate]);

  const togglesDisabled = true;

  const hasConferenceStarted = () => {
    const now = moment();
    const startAt = moment(conference.start_at);
    return now.isSameOrAfter(startAt);
  };

  const validateConferenceUpdate = () => {
    const hasStarted = hasConferenceStarted();

    if (hasStarted) {
      if (allowChat !== settings.allow_chat) {
        notifyError(t('general.something_wrong'));
        return false;
      }
      
      if (allowPolls !== settings.allow_polls) {
        notifyError(t('general.something_wrong'));
        return false;
      }

      if (allowInPersonJoining !== settings.allow_in_person_joining) {
        notifyError(t('general.something_wrong'));
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    if (conference?.id && allowPolls) {
      getConferencePolls(
        conference.id,
        (response) => {
          setPollsCount(response?.data?.polls?.length || 0);
        },
        (error) => {
          setPollsCount(0);
        }
      );
    } else {
      setPollsCount(0);
    }
  }, [conference?.id, allowPolls, getConferencePolls]);

  return (
    <ChatDetailsWrapper>
      <MeetingDetailsHeader
        handleClose={handleCloseConferenceDetails}
        handleSave={() => {
          if (!canEditConference) return;
    
          if (!validateConferenceUpdate()) {
            return;
          }
          handleUpdateConferenceDetails(
            conference.id,
            title,
            description,
            termsAndConditions,
            organizers,
            presenters,
            otherParticipants,
            conferenceStartDate,
            conferenceEndDate,
            {
              allow_mic: allowMic,
              allow_camera: allowCamera,
              allow_chat: allowChat,
              allow_polls: allowPolls,
              allow_in_person_joining: allowInPersonJoining,
              auto_approve_participants: allowAutoApprove,
              invite_all_org_users: inviteAllOrgUsers,
              public_registration: publicRegistration,
            },
            headerImageFile,
            headerImageRemoved
          )
        }}
        infoChanged={canEditConference && !updateBtnDisabled}
      />

      {editParticipants && (
        <ConferenceParticipants
          selectedParticipants={
            activeRole === CONFERENCE_ROLE_ORGANIZER ? organizers : presenters
          }
          initialParticipants={
            activeRole === CONFERENCE_ROLE_ORGANIZER ? initialOrganizers : initialPresenters
          }
          setSelectedParticipants={
            activeRole === CONFERENCE_ROLE_ORGANIZER ? setOrganizers : setPresenters
          }
          otherParticipants={activeRole === CONFERENCE_ROLE_ATTENDEE ? otherParticipants : []}
          setOtherParticipants={
            activeRole === CONFERENCE_ROLE_ATTENDEE ? setOtherParticipants : () => {}
          }
          initialOtherParticipants={
            activeRole === CONFERENCE_ROLE_ATTENDEE ? initialOtherParticipants : []
          }
          handleBackBtn={handleBackBtn}
          handleSaveBtn={() => {setEditParticipants(false)}}
          activeRole={activeRole}
          contacts={contacts}
          registrations={registrations}
          registrationsLoading={registrationsLoading}
          registrationActionsLoading={registrationActionsLoading}
          onApproveRegistration={handleApproveRegistrationAction}
          onDenyRegistration={handleDenyRegistrationAction}
          onUninviteRegistration={handleUninviteRegistrationAction}
        />
      )}
      {managePollsOpen && (
        <PollManagementModal
          conferenceId={conference.id}
          localPolls={[]}
          setLocalPolls={() => {}}
          onClose={() => {
            setManagePollsOpen(false);
            if (conference?.id && allowPolls) {
              getConferencePolls(
                conference.id,
                (response) => {
                  setPollsCount(response?.data?.polls?.length || 0);
                },
                () => {}
              );
            }
          }}
        />
      )}
      <Wrapper>
        <Group>
          <OptionWrapper>
            <CustomNameInput
              value={title}
              placeholder={t("new_chat.title")}
              onChange={(e) =>
                canEditConference && setTitle(e.currentTarget.value)
              }
              name="conferenceTitle"
              maxLength={100}
              readOnly={!canEditConference}
              $readonly={!canEditConference}
            ></CustomNameInput>
            <Separator />
            <CustomTextArea
              id="resizeTa-4"
              value={description}
              placeholder={t("general.details")}
              onChange={(e) =>
                canEditConference &&
                setDescription(e.currentTarget.value)
              }
              name="conferenceDetails"
              maxLength={1000}
              readOnly={!canEditConference}
              $readonly={!canEditConference}
            ></CustomTextArea>
            <Separator />
            <CustomTextArea
              id="resizeTa-terms-4"
              value={termsAndConditions}
              placeholder={t("general.terms_and_conditions")}
              onChange={(e) =>
                canEditTerms &&
                setTermsAndConditions(e.currentTarget.value)
              }
              name="conferenceTermsAndConditions"
              maxLength={5000}
              readOnly={!canEditTerms}
              $readonly={!canEditTerms}
            ></CustomTextArea>
          </OptionWrapper>
        </Group>
        {(canEditConference || hasHeaderImage) && (
          <Group>
            <OptionWrapper>
              {canEditConference && (
                <FileRow>
                  <FileLabel>{t("chat.image")}</FileLabel>
                  <FileActions>
                    <HiddenFileInput
                      ref={headerImageInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleHeaderImageChange}
                    />
                    <FileActionButton
                      type="button"
                      onClick={() => headerImageInputRef.current?.click()}
                    >
                      {hasHeaderImage ? t("settings.change") : t("general.add")}
                    </FileActionButton>
                    {hasHeaderImage && (
                      <FileActionButton type="button" onClick={clearHeaderImage}>
                        {t("general.remove")}
                      </FileActionButton>
                    )}
                  </FileActions>
                </FileRow>
              )}

              {headerImageFile?.name && (
                <>
                  <Separator />
                  <SelectedFileRow>
                    <SelectedFileName title={headerImageFile.name}>
                      {headerImageFile.name}
                    </SelectedFileName>
                  </SelectedFileRow>
                </>
              )}

              {hasHeaderImage && (
                <>
                  {(canEditConference || headerImageFile?.name) && (
                    <Separator />
                  )}
                  <PreviewContainer>
                    <PreviewImage
                      src={headerImagePreviewUrl || conference.header_image}
                      alt="Header"
                      loading="lazy"
                    />
                  </PreviewContainer>
                </>
              )}
            </OptionWrapper>
          </Group>
        )}
        <Group>
          <OptionWrapper
            onClick={
              canEditOrganizers
                ? () => {
                    setActiveRole(CONFERENCE_ROLE_ORGANIZER);
                    setEditParticipants(true);
                  }
                : undefined
            }
            style={{ cursor: canEditOrganizers ? "pointer" : "default" }}
          >
            <InvitesButton>
              {t("conferences.organizer")}
              <p>{organizers.length}</p>
              <RightArrow />
            </InvitesButton>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper
            onClick={
              canEditConference
                ? () => {
                    setActiveRole(CONFERENCE_ROLE_PRESENTER);
                    setEditParticipants(true);
                  }
                : undefined
            }
            style={{ cursor: canEditConference ? "pointer" : "default" }}
          >
            <InvitesButton>
              {t('conferences.presenters')}
              <p>{presenters.length}</p>
              <RightArrow />
            </InvitesButton>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <DateTimeWrapper>
              <DateInfo>{t("new_chat.starts")} </DateInfo>
              <MeetingDateTimePicker
                slim={true}
                format={"dd-MM-yyyy"}
                value={conferenceStartDate}
                setValue={setConferenceStartDate}
                noCalendar
                disabled={!canEditConference}
              />
              <TimePicker
                date={conferenceStartDate}
                setDate={setConferenceStartDate}
                minTime={minStartTime}
                minDateTime={now}
                readOnly={!canEditConference}
              />
            </DateTimeWrapper>
            <Separator />
            <DateTimeWrapper>
              <DateInfo>{t("new_chat.ends")}</DateInfo>
              <MeetingDateTimePicker
                slim={true}
                format={"dd-MM-yyyy"}
                value={conferenceEndDate}
                setValue={setConferenceEndDate}
                noCalendar
                disabled={!canEditConference}
              />
              <TimePicker
                date={conferenceEndDate}
                setDate={setConferenceEndDate}
                minTime={calculateMinEndTime()}
                minDateTime={minEndDateTime}
                readOnly={!canEditConference}
              />
            </DateTimeWrapper>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper
            onClick={
              canEditConference
                ? () => {
                    setActiveRole(CONFERENCE_ROLE_ATTENDEE);
                    setEditParticipants(true);
                  }
                : undefined
            }
            style={{ cursor: canEditConference ? "pointer" : "default" }}
          >
            <InvitesButton>
              {t('conferences.invitees')}
              <p>
                {invitees.length}
                {pendingRegistrationsCount > 0 &&
                  ` ( ${pendingRegistrationsCount} ${t("conferences.registration_pending").toLowerCase()} )`}
              </p>
              <RightArrow />
            </InvitesButton>
            <ParticipantsContainer>
              {invitees.map((item, index) => (
                <InvitedPeople key={index}>
                  <p>{item}</p>
                </InvitedPeople>
              ))}
            </ParticipantsContainer>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <OrganizerWrapper>
              <OrganizerTitle>{t("conferences.organizer")}</OrganizerTitle>
              <OrganizerText>
                {formatOrganizer(organizerData[0])}
              </OrganizerText>
            </OrganizerWrapper>
          </OptionWrapper>
        </Group>

        <Group>
          <OptionWrapper>
            <ToggleRow>
              <ToggleLabel>
                {t("conferences.allow_mic")}
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowMic}
                $disabled={togglesDisabled}
                onClick={
                  !togglesDisabled && canEditConference
                    ? () => setAllowMic((prev) => !prev)
                    : undefined
                }
                aria-pressed={allowMic}
              >
                <SwitchThumb $active={allowMic} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>
                {t("conferences.allow_camera")}
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowCamera}
                $disabled={togglesDisabled}
                onClick={
                  !togglesDisabled && canEditConference
                    ? () => setAllowCamera((prev) => !prev)
                    : undefined
                }
                aria-pressed={allowCamera}
              >
                <SwitchThumb $active={allowCamera} />
              </ToggleSwitch>
            </ToggleRow>
            <ToggleRow>
              <ToggleLabel>
                {t("conferences.allow_chat")}
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowChat}
                $disabled={hasConferenceStarted()}
                onClick={
                  !hasConferenceStarted() && canEditConference
                    ? () => setAllowChat((prev) => !prev)
                    : undefined
                }
                aria-pressed={allowChat}
              >
                <SwitchThumb $active={allowChat} />
              </ToggleSwitch>
            </ToggleRow>
            
            <ToggleRow>
              <ToggleLabel>
                {t("conferences.allow_polls")}
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowPolls}
                $disabled={hasConferenceStarted()}
                onClick={
                  !hasConferenceStarted() && canEditConference
                    ? () => setAllowPolls((prev) => !prev)
                    : undefined
                }
                aria-pressed={allowPolls}
              >
                <SwitchThumb $active={allowPolls} />
              </ToggleSwitch>
            </ToggleRow>

            <ToggleRow>
              <ToggleLabel>
                {t("conferences.allow_in_person_joining")}
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowInPersonJoining}
                $disabled={hasConferenceStarted() || !isCurrentOrganizer}
                onClick={
                  !hasConferenceStarted() && isCurrentOrganizer
                    ? () => setAllowInPersonJoining((prev) => !prev)
                    : undefined
                }
                aria-pressed={allowInPersonJoining}
              >
                <SwitchThumb $active={allowInPersonJoining} />
              </ToggleSwitch>
            </ToggleRow>

            <ToggleRow>
              <ToggleLabel>
                {t("conferences.auto_approve_participants")}
              </ToggleLabel>
              <ToggleSwitch
                type="button"
                $active={allowAutoApprove}
                $disabled={hasConferenceStarted()}
                onClick={
                  !hasConferenceStarted() && canEditConference
                    ? () => setAllowAutoApprove((prev) => !prev)
                    : undefined
                }
                aria-pressed={allowAutoApprove}
              >
                <SwitchThumb $active={allowAutoApprove} />
              </ToggleSwitch>
            </ToggleRow>
          </OptionWrapper>
        </Group>

        {allowPolls && (
          <Group>
            <OptionWrapper
              onClick={
                canEditConference
                  ? () => setManagePollsOpen(true)
                  : undefined
              }
              style={{ cursor: canEditConference ? "pointer" : "default" }}
            >
              <InvitesButton>
                {t('conferences.polls')}
                <p>{pollsCount}</p>
                <RightArrow />
              </InvitesButton>
            </OptionWrapper>
          </Group>
        )}

        {shareLink && (
          <Group>
            <OptionWrapper style={{ padding: "16px", alignItems: "center" }}>
              <QRCodeWrapper>
                <QRCode value={shareLink} size={200} />
              </QRCodeWrapper>
            </OptionWrapper>
          </Group>
        )}

        {shareLink && (
          <Group>
            <OptionWrapper style={{ flexDirection: "row" }}>
              <CustomNameInput
                value={shareLink}
                readOnly
                name="conferenceUrl"
                maxLength={100}
              ></CustomNameInput>
              <ShareBtn onClick={handleShareBtn}>
                {t("meetings.share")}
              </ShareBtn>
            </OptionWrapper>
          </Group>
        )}

        <ButtonsRow>
          {showJoinButton && (
            <PrimaryButton
              style={{ padding: "16px", flex: 1 }}
              onClick={() => handleJoinConference(conference)}
            >
              {t("conferences.join_conference")}
            </PrimaryButton>
          )}
          {canEditConference && (
            <DeleteConfirmationChild
              open={deleteConferencePopup}
              setOpen={setDeleteConferencePopup}
              variant={"delete_conference"}
              action={handleDeleteConferenceBtn}
              item={conference}
            >
              <PrimaryButtonRemove
                style={{ padding: "16px", flex: 1 }}
                onClick={() => setDeleteConferencePopup(true)}
              >
                {t("general.delete")}
              </PrimaryButtonRemove>
            </DeleteConfirmationChild>
          )}
        </ButtonsRow>
      </Wrapper>
    </ChatDetailsWrapper>
  );
};

export default ConferenceDetailsCard;

const ChatDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.chat.conversationBackground};
  height: 100%;
  z-index: 1;
`;

const ParticipantsContainer = styled.div`
  padding-bottom: 8px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 0 auto;
  padding: 21px;
  width: 100%;
  max-width: 631px;
  height: 100%;
  overflow: auto;
`;

const Group = styled.div`
  display: flex;
  border-radius: 4px;
  align-items: center;
  width: 100%;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
  width: 100%;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
`;

const FileLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
`;

const FileActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileActionButton = styled.button`
  border: none;
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 12px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
`;

const SelectedFileRow = styled.div`
  padding: 12px 16px;
`;

const SelectedFileName = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewContainer = styled.div`
  padding: 12px 16px;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const CustomInput = styled.input`
  background: none;
  border: none;
  color: ${({ theme, $readonly }) =>
    $readonly ? theme.menu.secondaryTextColor : theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.40799999237060547px;
  width: 100%;
  padding: 15px 16px;
  opacity: ${({ $readonly }) => ($readonly ? 0.7 : 1)};
  cursor: ${({ $readonly }) => ($readonly ? "default" : "text")};
  caret-color: ${({ $readonly }) => ($readonly ? "transparent" : "auto")};
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const CustomNameInput = styled(CustomInput)`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const DateTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
`;

const DateInfo = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const InvitesButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 5px;
  border: none;
  padding: 14px 16px;
  cursor: pointer;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  font-size: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  gap: 10px;
  width: 100%;
  text-align: left;
  p {
    margin-left: auto;
  }
  svg path {
    stroke: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const Separator = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${({ theme }) => theme.sidebar.borderColor};
`;

const CustomTextArea = styled.textarea`
  padding: 15px 16px;
  width: 100%;
  resize: none;
  background: none;
  min-height: 80px;
  border: none;
  color: ${({ theme, $readonly }) =>
    $readonly ? theme.menu.secondaryTextColor : theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  max-height: 250px;
  opacity: ${({ $readonly }) => ($readonly ? 0.7 : 1)};
  cursor: ${({ $readonly }) => ($readonly ? "default" : "text")};
  caret-color: ${({ $readonly }) => ($readonly ? "transparent" : "auto")};
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const OrganizerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px 16px;
  width: 100%;
`;
const OrganizerTitle = styled.h4`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  text-transform: uppercase;
`;
const OrganizerText = styled.p`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InvitedPeople = styled.div`
  overflow: hidden;
  display: flex;
  width: 100%;
  padding: 4px 16px;
  p {
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: left;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 16px;
`;

const ToggleLabel = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? (theme.premium_green || '#11B973') : theme.sidebar.borderColor};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background: ${({ $active, theme }) =>
    $active ? (theme.premium_green || '#11B973') : theme.menu.groupContentBackground};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: background 0.2s ease-in-out, border 0.2s ease-in-out;
  padding: 0;
`;

const SwitchThumb = styled.span`
  position: absolute;
  top: 50%;
  left: ${({ $active }) => ($active ? '22px' : '2px')};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $active, theme }) =>
    $active ? '#FFFFFF' : (theme.menu.secondaryTextColor || '#777')};
  transform: translateY(-50%);
  transition: left 0.2s ease-in-out, background 0.2s ease-in-out, transform 0.2s ease-in-out;
`;

const QRCodeWrapper = styled.div`
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.menu.background};
  padding: 8px;
  border-radius: 4px;
`;

const ShareBtn = styled.button`
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: none;
  cursor: pointer;
  padding: 14px 15px 15px 0px;
  border-radius: 10px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;
